import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Footer from '../Common/Footer';

const NotFound = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('pages');

  return (
    <div className="flex flex-col min-h-screen bg-[#F8F8F7]">
      {/* Main Content */}
      <section className="flex-1 flex flex-col items-center justify-center px-6 py-16">
        <div className="max-w-lg w-full text-center">
          {/* 404 Illustration with food theme */}
          <div className="mb-8">
            <div className="relative inline-block">
              {/* Plate/bowl icon */}
              <div className="w-32 h-32 mx-auto mb-4 rounded-full bg-white shadow-[0px_1px_4px_1px_rgba(35,33,22,0.06)] flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none">
                  <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20Z" fill="#E5E5E5"/>
                  <path d="M12 6C8.69 6 6 8.69 6 12C6 15.31 8.69 18 12 18C15.31 18 18 15.31 18 12C18 8.69 15.31 6 12 6ZM12 16C9.79 16 8 14.21 8 12C8 9.79 9.79 8 12 8C14.21 8 16 9.79 16 12C16 14.21 14.21 16 12 16Z" fill="#878683"/>
                  <path d="M12 10C10.9 10 10 10.9 10 12C10 13.1 10.9 14 12 14C13.1 14 14 13.1 14 12C14 10.9 13.1 10 12 10Z" fill="#009900"/>
                </svg>
              </div>
            </div>
            <div className="text-[80px] md:text-[100px] font-bold text-[#0D0C07] leading-none tracking-[-0.03em]">
              {t('notFound.title')}
            </div>
          </div>

          {/* Message */}
          <h1 className="text-[24px] md:text-[28px] font-bold text-[#0D0C07] mb-3 leading-tight">
            {t('notFound.headline')}
          </h1>
          <p className="text-[#878683] text-[16px] md:text-[18px] leading-[28px] mb-8 max-w-md mx-auto">
            {t('notFound.message')}
          </p>

          {/* Primary CTA - matching LandingPage green button */}
          <button
            onClick={() => navigate('/')}
            className="
              inline-flex justify-center items-center gap-2
              h-[56px] px-[40px] py-[14px] mb-4
              rounded-[32px]
              border-[1px]
              bg-[#090]
              text-[#FFF] text-center
              text-[14px] font-medium leading-[28px]
              transition-colors
              md:hover:bg-[#060]
              bg-[linear-gradient(200deg,_#090_0%,_#060_100%)]
              shadow-[0_2px_16px_0_rgba(0,0,0,0.12)_inset,_0_0_32px_0_rgba(255,255,255,0.4)_inset,_0_0px_0px_2px_rgba(0,102,0,0.4)]
            "
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M9 22V12H15V22" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>{t('notFound.backHome')}</span>
          </button>

          {/* Secondary action */}
          <div className="mt-4">
            <button
              onClick={() => navigate(-1)}
              className="
                inline-flex items-center gap-2
                text-[#0D0C07] text-[14px] font-medium
                md:hover:text-[#009900]
                transition-colors
              "
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {t('notFound.goBack')}
            </button>
          </div>

          {/* Quick Links */}
          <div className="mt-12 pt-8 border-t border-[#E5E5E5]">
            <p className="text-[14px] text-[#878683] mb-4">{t('notFound.explore')}</p>
            <div className="flex flex-wrap justify-center gap-3">
              <button
                onClick={() => navigate('/wizard')}
                className="
                  px-5 py-2.5 
                  text-[14px] text-[#0D0C07] font-medium
                  bg-white rounded-full 
                  border border-[#E5E5E5] 
                  md:hover:bg-[#F5F5F5] md:hover:border-[#D5D5D5]
                  transition-colors
                  shadow-[0px_1px_2px_0px_rgba(35,33,22,0.04)]
                "
              >
                {t('notFound.createMealPlan')}
              </button>
              <button
                onClick={() => navigate('/aboutus')}
                className="
                  px-5 py-2.5 
                  text-[14px] text-[#0D0C07] font-medium
                  bg-white rounded-full 
                  border border-[#E5E5E5] 
                  md:hover:bg-[#F5F5F5] md:hover:border-[#D5D5D5]
                  transition-colors
                  shadow-[0px_1px_2px_0px_rgba(35,33,22,0.04)]
                "
              >
                {t('about.title')}
              </button>
              <button
                onClick={() => navigate('/contact')}
                className="
                  px-5 py-2.5 
                  text-[14px] text-[#0D0C07] font-medium
                  bg-white rounded-full 
                  border border-[#E5E5E5] 
                  md:hover:bg-[#F5F5F5] md:hover:border-[#D5D5D5]
                  transition-colors
                  shadow-[0px_1px_2px_0px_rgba(35,33,22,0.04)]
                "
              >
                {t('contact.title')}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer - matching LandingPage */}
      <Footer />
    </div>
  );
};

export default NotFound;
